const BiotechUnit1Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <div>
        <h2 class="text-2xl font-black text-purple-900 mb-3 border-b-2 border-slate-200 pb-2">Unit I: Introduction to Biotechnology, Enzyme Engineering & Genetic Engineering</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            <strong>Pharmaceutical Biotechnology</strong> is the application of biotechnology principles (using living organisms, cells, or molecular analogues) to manufacture therapeutic products like vaccines, monoclonal antibodies, and recombinant proteins.
        </p>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-purple-800 mb-3">1. Biotechnology with reference to Pharmaceutical Sciences</h3>
        <p class="text-[13px] text-slate-700 mb-3 leading-relaxed text-justify">
            Biotechnology plays a pivotal role in modern pharmacy by shifting drug discovery from accidental chemical synthesis to rational biological design. 
        </p>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-[13px] text-slate-700">
            <div class="bg-purple-50 p-4 rounded border border-purple-100">
                <p class="font-bold text-purple-900 mb-1">Historical/Traditional Biotech:</p>
                <p class="text-[12px] text-slate-600">Utilizes natural capabilities of intact microorganisms. Examples include traditional fermentation for producing antibiotics (Penicillin), alcohol, and vitamins.</p>
            </div>
            <div class="bg-purple-50 p-4 rounded border border-purple-100">
                <p class="font-bold text-purple-900 mb-1">Modern Biotechnology:</p>
                <p class="text-[12px] text-slate-600">Utilizes genetically altered cells or organisms through **Recombinant DNA (rDNA) technology** and hybridoma technology. Examples include insulin production, vaccines, and gene therapy.</p>
            </div>
        </div>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-purple-800 mb-3">2. Enzyme Biotechnology & Immobilization</h3>
        <p class="text-[13px] text-slate-700 mb-3">
            <strong>Enzyme Immobilization</strong> is the restriction of enzyme mobility in a fixed space or matrix, allowing them to be reused continuously while maintaining their catalytic activity.
        </p>
        
        <h4 class="text-[15px] font-bold text-purple-900 mb-2">Methods of Enzyme Immobilization:</h4>
        <div class="overflow-x-auto rounded-lg border border-slate-200 mb-4">
            <table class="min-w-full bg-white text-[13px] text-left">
                <thead class="bg-purple-100 text-purple-900">
                    <tr>
                        <th class="px-4 py-2.5 font-bold w-1/4">Method Class</th>
                        <th class="px-4 py-2.5 font-bold w-1/3">Mechanism</th>
                        <th class="px-4 py-2.5 font-bold">Advantages & Disadvantages</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 text-slate-600">
                    <tr>
                        <td class="px-4 py-3 font-bold text-purple-900">Adsorption (Physical)</td>
                        <td class="px-4 py-3">Enzymes bound to the surface of support matrix via weak forces (Van der Waals, Hydrogen bonds). Carriers: Charcoal, Alumina.</td>
                        <td class="px-4 py-3"><span class="text-green-700 font-bold">Adv:</span> Simple, no damage to enzyme structure.<br><span class="text-red-600 font-bold">Disadv:</span> Weak binding, enzymes can easily leak out (desorption).</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-purple-900">Covalent Binding</td>
                        <td class="px-4 py-3">Chemical bond formed between functional groups of the enzyme and the support matrix. Uses cross-linking agents like glutaraldehyde.</td>
                        <td class="px-4 py-3"><span class="text-green-700 font-bold">Adv:</span> Strong binding, no leakage.<br><span class="text-red-600 font-bold">Disadv:</span> Harsh chemical reactions may alter active sites, decreasing activity.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-purple-900">Entrapment</td>
                        <td class="px-4 py-3">Enzymes are physically trapped inside a porous polymeric network meshwork. Common matrix: Polyacrylamide gel, Calcium alginate beads.</td>
                        <td class="px-4 py-3"><span class="text-green-700 font-bold">Adv:</span> Safe for most enzymes.<br><span class="text-red-600 font-bold">Disadv:</span> Diffusion barrier; large substrate molecules cannot reach the enzyme.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-purple-900">Encapsulation</td>
                        <td class="px-4 py-3">Enzymes are enclosed inside a semi-permeable membrane microcapsule (e.g., liposomes, nitrocellulose capsules).</td>
                        <td class="px-4 py-3"><span class="text-green-700 font-bold">Adv:</span> Can hold a large volume of enzymes.<br><span class="text-red-600 font-bold">Disadv:</span> Membrane permeability restricts substrate size.</td>
                    </tr>
                </tbody>
            </table>
        </div>
        
        <p class="text-[12px] bg-slate-50 p-2.5 rounded border border-slate-100 text-slate-700">
            <strong>Pharmaceutical Applications:</strong> Used in the synthesis of semi-synthetic penicillins (using immobilized penicillin acylase), production of diagnostic kits, and treatment of specific metabolic disorders (Enzyme Replacement Therapy).
        </p>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-purple-800 mb-3">3. Biosensors in Pharmaceutical Industries</h3>
        <p class="text-[13px] text-slate-700 mb-3">
            A <strong>Biosensor</strong> is an analytical device that combines a biological recognition element with a physicochemical transducer to produce a measurable signal proportional to the concentration of a target analyte.
        </p>

        <div class="bg-purple-50 p-4 rounded-xl border border-purple-100 text-[13px] text-slate-700 mb-4">
            <p class="font-bold text-purple-900 mb-2">Basic Components and Working:</p>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 text-center font-semibold text-[12px]">
                <div class="bg-white p-2.5 rounded border border-purple-200">
                    <p class="text-purple-800">1. Bioreceptor</p>
                    <p class="text-slate-500 font-normal mt-1">Biological element (Enzyme, Antibody, DNA) that binds specifically to the target analyte.</p>
                </div>
                <div class="bg-white p-2.5 rounded border border-purple-200">
                    <p class="text-purple-800">2. Transducer</p>
                    <p class="text-slate-500 font-normal mt-1">Converts the bio-recognition event into an electrical, optical, or thermal signal.</p>
                </div>
                <div class="bg-white p-2.5 rounded border border-purple-200">
                    <p class="text-purple-800">3. Signal Processor</p>
                    <p class="text-slate-500 font-normal mt-1">Amplifies and processes the electrical signal, displaying it on a digital screen.</p>
                </div>
            </div>
        </div>

        <p class="text-[13px] text-slate-700 font-bold mb-1">Applications:</p>
        <ul class="list-disc pl-5 space-y-1 text-[13px] text-slate-600">
            <li><strong>Blood Glucose Monitoring:</strong> Handheld glucometers utilize glucose oxidase immobilized on an electrochemical transducer.</li>
            <li><strong>Quality Control:</strong> Quantitative analysis of raw materials, antibiotics, and vitamins in pharmaceutical formulations.</li>
            <li><strong>Environmental Monitoring:</strong> Detection of toxic waste, microbial load, and heavy metals in industrial effluents.</li>
        </ul>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-purple-800 mb-3">4. Brief Introduction to Protein Engineering</h3>
        <p class="text-[13px] text-slate-700 mb-3 leading-relaxed text-justify">
            <strong>Protein Engineering</strong> is the process of developing useful or valuable proteins through the modification of amino acid sequences via recombinant DNA technologies. Its core goal is to design proteins with enhanced stability, altered substrate specificity, or reduced immunogenicity.
        </p>
        <div class="bg-slate-50 p-4 rounded-lg border border-slate-200 text-[13px] text-slate-700">
            <p class="font-bold text-purple-900 mb-2">Two Main Approaches:</p>
            <ul class="list-disc pl-5 space-y-2">
                <li><strong>Rational Design:</strong> Involves making specific, planned mutations based on detailed knowledge of the protein's 3D structure (obtained via X-ray crystallography or computer modeling). uses site-directed mutagenesis.</li>
                <li><strong>Directed Evolution:</strong> Emulates natural selection in a laboratory. Random mutations are introduced into a gene (using error-prone PCR), creating a vast library of variants, which are then screened for desired characteristics.</li>
            </ul>
        </div>
    </div>

    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-purple-800 mb-3">5. Use of Microbes in Industry: Production of Enzymes</h3>
        <p class="text-[13px] text-slate-700 mb-3">Microorganisms function as industrial bio-factories for enzyme production via large-scale submerged or solid-state fermentation processes.</p>
        
        <div class="overflow-x-auto rounded-lg border border-slate-200">
            <table class="min-w-full bg-white text-[13px] text-left">
                <thead class="bg-slate-100 text-slate-700">
                    <tr>
                        <th class="px-4 py-2.5 font-bold w-1/4">Enzyme</th>
                        <th class="px-4 py-2.5 font-bold w-1/3">Microbial Source</th>
                        <th class="px-4 py-2.5 font-bold">Industrial / Pharmaceutical Application</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 text-slate-600">
                    <tr>
                        <td class="px-4 py-3 font-bold text-purple-900">Amylase</td>
                        <td class="px-4 py-3"><em>Bacillus subtilis</em>, <em>Aspergillus oryzae</em></td>
                        <td class="px-4 py-3">Hydrolyzes starch into sugars. Used in digestive aids, textile, and baking industries.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-purple-900">Catalase</td>
                        <td class="px-4 py-3"><em>Aspergillus niger</em>, <em>Micrococcus lysodeikticus</em></td>
                        <td class="px-4 py-3">Breaks down hydrogen peroxide into water and oxygen. Used in food preservation and contact lens cleaning solutions.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-purple-900">Peroxidase</td>
                        <td class="px-4 py-3">Horseradish (plant), <em>Phanerochaete chrysosporium</em></td>
                        <td class="px-4 py-3">Used extensively in clinical diagnostic kits (ELISA assays) coupled with antibodies.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-purple-900">Lipase</td>
                        <td class="px-4 py-3"><em>Candida rugosa</em>, <em>Rhizopus oryzae</em></td>
                        <td class="px-4 py-3">Digests fats/lipids. Used in digestive enzyme supplements and formulation of eco-friendly detergents.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-purple-900">Protease</td>
                        <td class="px-4 py-3"><em>Bacillus licheniformis</em>, <em>Aspergillus niger</em></td>
                        <td class="px-4 py-3">Cleaves proteins. Used in wound debridement ointments, anti-inflammatory formulations, and detergent processing.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-purple-900">Penicillinase</td>
                        <td class="px-4 py-3"><em>Bacillus cereus</em></td>
                        <td class="px-4 py-3">Breaks the &beta;-lactam ring of penicillin. Used as an antidote for penicillin hypersensitivity / toxicity and in diagnostic kits.</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="bg-purple-950 text-white p-6 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black mb-3">6. Basic Principles of Genetic Engineering</h3>
        <p class="text-[13px] text-purple-200 mb-4 leading-relaxed">
            Genetic Engineering (Recombinant DNA Technology) involves altering the genetic makeup of an organism by introducing foreign DNA fragments into its genome to produce desired proteins.
        </p>
        
        <div class="space-y-3 text-[13px]">
            <p class="font-bold text-purple-300">The Step-by-Step Core Process:</p>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-slate-800">
                <div class="bg-white p-3 rounded shadow-sm">
                    <p class="font-bold text-purple-900">Step 1: Isolation of DNA</p>
                    <p class="text-[12px] text-slate-600 mt-1">The gene of interest (target gene) is isolated from the donor organism, and plasmid DNA is isolated from bacteria (acting as a vector).</p>
                </div>
                <div class="bg-white p-3 rounded shadow-sm">
                    <p class="font-bold text-purple-900">Step 2: Cleavage with Restriction Enzymes</p>
                    <p class="text-[12px] text-slate-600 mt-1">Both the target gene and the vector are cleaved at specific sites using the same **Restriction Endonucleases** ("molecular scissors"), generating compatible sticky ends.</p>
                </div>
                <div class="bg-white p-3 rounded shadow-sm">
                    <p class="font-bold text-purple-900">Step 3: Ligation (Gene Joining)</p>
                    <p class="text-[12px] text-slate-600 mt-1">The isolated target gene is inserted into the cut vector and joined permanently using the enzyme **DNA Ligase** ("molecular glue"), forming recombinant DNA.</p>
                </div>
                <div class="bg-white p-3 rounded shadow-sm">
                    <p class="font-bold text-purple-900">Step 4: Transformation & Cloning</p>
                    <p class="text-[12px] text-slate-600 mt-1">The recombinant vector is introduced into a suitable host cell (e.g., <em>E. coli</em> or yeast). The host cell divides, cloning millions of copies of the target gene to express the final recombinant protein (e.g., Human Insulin).</p>
                </div>
            </div>
        </div>
    </div>

</div>
`;

export default BiotechUnit1Notes;